import React, { useCallback, useMemo, useState } from 'react';
import { useGlobalState } from '../GlobalState';
import { Button, notification } from 'antd';
import axios from 'axios';
import { getFullDate, toUTCDate } from '../../../../utils/date';
import { exotic_fishery } from '../../../common/Utils';
import SvgCalendar from '../../../../assets/images/Icons/SVG/Calendar';
import CalendarColoursModal from './CalendarColoursModal';
import LakeBookingInformationModal from './LakeBookingInformationModal';
import SvgUserBox from '../../assets/images/UserBox';
import AvailabilityCalendar, {
  emptyDatesObject,
} from '../../../availability_calendar/AvailabilityCalendar';
import moment from 'moment';
import { useLocalStorage } from '../../../../utils/hooks';
import { useGetDynamicBookingTextQuery } from '../../../user_profile/userProfileSlice';

const errorNotification = ({
  message = 'There was an issue!',
  description = undefined,
}) =>
  notification.error({
    message: <div className='gSemiBold'>{message}</div>,
    style: { border: '1px #D34545 solid' },
    placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
    className: 'gRegular br10',
    description: description,
  });

const warningNotification = ({
  message = 'Please be aware!',
  description = undefined,
}) =>
  notification.warning({
    message: <div className='gSemiBold'>{message}</div>,
    style: { border: '1px #F7C046 solid' },
    placement: window?.innerWidth <= 500 ? 'bottom' : 'topRight',
    className: 'gRegular br10',
    description: description,
  });

export default function SelectDate({
  maxVisibleDate,
  maxBookableDate,
  maxBookableErrorMessage,
  fishery_id,
  customSlotId,
  registerTimeRequest,
  unregisterTimeRequest,
}) {
  const [bookingDates, setBookingDates] = useGlobalState('bookingDates');
  const [userSelectedPakage] = useGlobalState('userSelectedPakage');
  const [disabledDates, setDisabledDates] = useGlobalState('disabledDates');
  const [limitedDates, setLimitedDates] = useGlobalState('limitedDates');
  const [accommodation] = useGlobalState('accommodation');
  const [, setAvailableTime] = useGlobalState('availableTime');
  const [, setArrivalTime] = useGlobalState('arrivalTime');
  const [, setDepartureTime] = useGlobalState('departureTime');
  const [, setEstimatedPricing] = useGlobalState('estimatedPricing');
  const [, setUserBookingData] = useGlobalState('userBookingData');
  const [, setUserSelectedSwims] = useGlobalState('userSelectedSwims');
  const [, setUserSelectedDoubleSwim] = useGlobalState(
    'userSelectedDoubleSwim'
  );
  const [userSelectedRods] = useGlobalState('userSelectedRods');
  const [loading, setBookingsLoading] = useGlobalState('bookingsLoading');
  const [, setTicketAvailable] = useGlobalState('ticketsAvailable');
  const [dateText] = useGlobalState('dateText');

  const [howToBook] = useGlobalState('howToBook');
  const [userSelectedLakeId] = useGlobalState('userSelectedLakeId');
  const [userSelectedAnglers] = useGlobalState('userSelectedAnglers');
  const [current, setCurrent] = useGlobalState('current');
  const [lakeExclusive] = useGlobalState('lakeExclusive');
  const [lakeDetails, setLakeDetails] = useGlobalState('lakeDetails');
  const [bookingType] = useGlobalState('bookingType');
  const is_exotic_arapaima_lake =
    userSelectedLakeId === 'a4688274-3f67-40a5-be8d-a8dc51c053b3';
  const { data: DynamicText } = useGetDynamicBookingTextQuery(fishery_id);
  const axios_config = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  };

  const getTimeAvailability = (data) => {
    registerTimeRequest();
    axios
      .post(
        'booking/lake/availability',
        {
          ...data,
          ...(userSelectedPakage && {
            custom_slot_id:
              lakeDetails?.custom_slot_days[userSelectedPakage - 1]?.id,
          }),
        },
        axios_config
      )
      .then((res) => {
        if (res.status === 200) {
          let res_data = res.data.result;

          if (res_data.is_available) {
            setAvailableTime(res_data);
          } else {
            warningNotification({
              description: res_data.message,
            });
          }
        }
      })
      .catch((err) => {
        if (err.response) {
          if (err.response.status === 500) {
            errorNotification({
              description: 'Server Response failed. Please try again later',
            });
          } else if (err.response.status === 401) {
            warningNotification({
              description:
                'Your action will not be saved. Please Login and try again!',
            });
          } else if (err.response.status === 400) {
            warningNotification({
              description: err.response.data['detail'],
            });
          } else if (err.response.status === 422) {
            if (err.response.data['detail'] === 'Signature has expired') {
              errorNotification({
                description:
                  'Login session has been expired. Please Login to continue!',
              });
            } else {
              errorNotification({
                description:
                  "OOPS! Server didn't like the request. If this persists please contact support team",
              });
            }
          } else if (err.response.status === 423) {
            errorNotification({
              description:
                'Sorry ! this booking slot has been booked by another user, Try different booking!',
            });
          }
        } else {
          errorNotification({
            message: 'Error processing result',
          });
        }
      })
      .finally(() => {
        unregisterTimeRequest();
      });
  };

  const handleDateChange = (selectedRange) => {
    // Reset timing
    setAvailableTime(null);
    setArrivalTime(null);
    setDepartureTime(null);
    setEstimatedPricing(null);

    const startDate = getFullDate(selectedRange.startDate);

    const endDate = getFullDate(selectedRange.endDate);

    if (moment(selectedRange.startDate).isSame(selectedRange.endDate, 'day')) {
      const noNightDate = limitedDates
        .filter((ld) => ld.type === 'no-night')
        .find((ld) =>
          moment(ld.date).isSame(toUTCDate(selectedRange.startDate), 'day')
        );
      if (noNightDate) {
        warningNotification({
          description:
            'The next night session is fully booked. You can still select this date for a day session.',
        });
      }
    }

    let payload_data = {
      anglers: userSelectedAnglers,
      guest: userSelectedRods,
      start_date: startDate,
      end_date: endDate,
      lake_id: userSelectedLakeId,
      book_lake_exclusive: lakeExclusive,
      booking_type: bookingType,
    };
    // Keeps array reference invariant to prevent calendar month reset
    bookingDates[0] = selectedRange;
    setBookingDates(bookingDates);
    getTimeAvailability(payload_data);
    setUserBookingData({});
    setUserSelectedSwims([]);
    setUserSelectedDoubleSwim([]);
  };

  const getBlockedBookingDates = (date) => {
    let _date = moment(date).format('YYYY-MM-DD');
    // return;
    setBookingsLoading(true);
    const axios_config = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    };

    let payload_data = {
      anglers: userSelectedAnglers,
      guest: userSelectedRods,
      lake_id: userSelectedLakeId,
      date: _date,
      book_lake_exclusive: lakeExclusive,
      custom_slot_id: customSlotId,
      booking_type: bookingType,
    };

    axios
      .post('booking/lake/details', payload_data, axios_config)
      .then((res) => {
        if (res.status === 200) {
          const _result = res.data.result;
          setDisabledDates([
            ...disabledDates,
            ..._result.fully_booked_dates.map(toUTCDate).filter(
              /* Don't add dates which have already been stored */
              (d) =>
                !disabledDates.some(
                  (prevDate) => prevDate.toISOString() === d.toISOString()
                )
            ),
          ]);
          setLimitedDates([
            ...limitedDates,
            ...(
              _result?.limited_availability_dates?.map((ld) => ({
                ...ld,
                date: toUTCDate(ld.date),
              })) || []
            ).filter(
              /* Don't add dates which have already been stored */
              (ld) =>
                !limitedDates.some(
                  (prevLDate) =>
                    prevLDate.date.toISOString() === ld.date.toISOString()
                )
            ),
          ]);
          setLakeDetails(_result);
          setTicketAvailable(_result.swims_tickets_available);
        } else {
          errorNotification({
            description: 'Error processing result',
          });
        }
      })
      .catch((err) => {
        console.error(err);
        if (err.response) {
          if (err.response.status === 401 || err.response.status === 422) {
            errorNotification({
              description: 'Please login to continue',
            });
          }
        } else {
          errorNotification({
            description: 'Error processing result',
          });
        }
      })
      .finally(() => setBookingsLoading(false));
  };

  const clearSelectedDates = () => {
    if (!bookingDates?.[0]?.startDate) {
      return;
    }
    // Keeps the object reference invariant to prevent calendar re-render (weird bug with DateRangePicker)
    bookingDates[0] = { ...emptyDatesObject };
    setBookingDates(bookingDates);
    setEstimatedPricing(null);
    setAvailableTime(null);
    setArrivalTime(null);
    setDepartureTime(null);
  };

  const wordAndCount = (word, count) =>
    `${count} ${word}${count === 1 ? '' : 's'}`;

  const anglerCountNotice = (
    <div className='gSemiBold fs-14 justify-between row-center'>
      <div className={'row-center'}>
        <SvgUserBox
          className={'mg-right-10 fs-18'}
          style={{ marginBottom: '1px' }}
        />
        <span>
          Showing availability for{' '}
          {accommodation
            ? 'your selection'
            : DynamicText?.is_active &&
                DynamicText?.properties?.booking_step_2?.booking_type
              ? DynamicText?.properties?.booking_step_2?.booking_type
              : lakeExclusive
                ? 'Lake Exclusive'
                : fishery_id === exotic_fishery && is_exotic_arapaima_lake
                  ? '2 anglers'
                  : fishery_id === exotic_fishery
                    ? ` ${wordAndCount('guest', userSelectedAnglers)}`
                    : ` ${wordAndCount('angler', userSelectedAnglers)}`}
        </span>
      </div>
      <Button type={'text'} className={'w-fit pd-left-0 pd-right-0'}>
        <span
          className={'txt-underline fs-15'}
          onClick={() => setCurrent(current - 1)}
        >
          Edit
        </span>
      </Button>
    </div>
  );

  const explanationLinkStyle = 'txt-underline blue-2 gSemiBold fs-15 cp';

  const [showColoursExplanationModal, setShowColoursExplanationModal] =
    useState(false);
  const coloursExplanation = (
    <>
      <CalendarColoursModal
        visible={showColoursExplanationModal}
        onClose={() => setShowColoursExplanationModal(false)}
      />
      <Button
        type={'text'}
        className={'pd-left-0 pd-right-0 w-fit h-fit text-wrap txt-left'}
      >
        <span
          className={explanationLinkStyle}
          onClick={() => setShowColoursExplanationModal(true)}
        >
          What do the colours mean?
        </span>
      </Button>
    </>
  );

  const [seenInformationMap, setSeenInformationMap] = useLocalStorage(
    'lakeInfoMap',
    {}
  );
  const updateLakeInfoSeenMap = useCallback(
    (lake_id, value) => {
      setSeenInformationMap((prev) => {
        const copy = Object.assign({}, prev);
        copy[lake_id] = value;
        return copy;
      });
    },
    [setSeenInformationMap]
  );

  const [showBookingInfoModal, setShowBookingInfoModal] = useState(
    !seenInformationMap?.[userSelectedLakeId]
  );
  const bookingInformationExplanation = (
    <>
      <LakeBookingInformationModal
        visible={howToBook && showBookingInfoModal}
        onClose={() => {
          setShowBookingInfoModal(false);
          updateLakeInfoSeenMap(userSelectedLakeId, true);
        }}
        bookingInfo={howToBook}
      />
      {userSelectedLakeId && howToBook > '' && (
        <Button
          type={'text'}
          className={'pd-left-0 pd-right-0 w-fit h-fit text-wrap txt-left'}
        >
          <span
            className={explanationLinkStyle}
            role={'button'}
            onClick={() => setShowBookingInfoModal(true)}
          >
            View Important Booking Information
          </span>
        </Button>
      )}
    </>
  );

  return (
    <>
      <div className='booking-time-heading mg-bottom-8'>
        <SvgCalendar className={'fs-16 white'} />
        <span>Step 1: Select Dates</span>
      </div>
      <div className={'display-flex-column'}>
        {anglerCountNotice}
        {!accommodation && coloursExplanation}
        {bookingInformationExplanation}
      </div>
      <AvailabilityCalendar
        reRenderKey={`${dateText}${userSelectedLakeId}${userSelectedAnglers}${lakeExclusive}${userSelectedPakage}`}
        selectedRanges={bookingDates}
        fullyBookedDates={disabledDates}
        limitedAvailabilityDates={limitedDates}
        selectedPackage={
          lakeDetails?.custom_slot_days?.[
            userSelectedPakage ? userSelectedPakage - 1 : 0
          ] ?? null
        }
        loading={loading}
        maxDate={maxVisibleDate}
        maxSelectableDate={maxBookableDate}
        maxDateSelectMessage={maxBookableErrorMessage}
        onDateSelection={handleDateChange}
        onMonthChange={getBlockedBookingDates}
        clearSelectedDates={clearSelectedDates}
        showSelectionTail={lakeDetails?.is_24_hour_lake}
      />
    </>
  );
}
